import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { utc } from "@/components/sync/syncText";

export const stepUp = async (code) => (await api.post("/auth/gtf-step-up", { totp_code: code })).data.step_up_token;
export const fail = (e) => toast.error(formatApiError(e?.response?.data?.detail) || "Request failed");
const td = "px-2 py-1.5 border-t border-[var(--border-subtle)] align-top";
const sel = "h-8 text-xs bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded px-2";
const CLS = { mandatory: "text-rose-300", optional_confirmation: "text-amber-300", contextual: "text-sky-300", unresolved: "text-fuchsia-300", educational: "text-slate-300" };
const APPR = { approved: "text-emerald-300", rejected: "text-slate-500", pending: "text-amber-300" };

export function useRefresh() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith("gtfr") });
}

export function CodeInput({ value, onChange, testId }) {
  return <Input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit code" inputMode="numeric" className="h-8 w-28 text-xs font-mono" />;
}

export function Stepper({ states, state }) {
  const at = state ? states.indexOf(state) : -1;
  return (
    <ol data-testid="gtfr-stepper" className="flex flex-wrap gap-1 text-[10px] font-mono">
      {states.map((s, i) => (
        <li key={s} data-testid={`gtfr-step-${s}`} className={`rounded px-2 py-1 border ${i <= at ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" : "border-[var(--border-subtle)] text-slate-500"}`}>{i + 1}. {s}</li>
      ))}
    </ol>
  );
}

export function ChapterList({ data, selected, onSelect }) {
  return (
    <div data-testid="gtfr-chapters" className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
      {data.chapters.map((c) => (
        <button key={c.module} data-testid={`gtfr-chapter-${c.module}`} onClick={() => onSelect(c.module)}
          className={`text-left rounded-lg border p-3 transition-colors duration-200 ${selected === c.module ? "border-emerald-500/50 bg-emerald-500/5" : "border-[var(--border-subtle)] hover:border-slate-500"}`}>
          <div className="flex items-center justify-between text-xs"><span className="font-mono text-slate-400">Module {c.module}</span><span data-testid={`gtfr-chapter-state-${c.module}`} className="font-mono text-emerald-300">{c.state || "AWAITING SOURCE"}</span></div>
          <div className="mt-1 text-sm text-slate-100">{c.title}</div>
          <div className="text-[11px] text-slate-500">{c.source_name} · {c.time_range}</div>
          <div className="mt-2 h-1 rounded bg-slate-800"><div className="h-1 rounded bg-emerald-400" style={{ width: `${c.progress_pct}%` }} /></div>
          <div className="mt-2 flex flex-wrap gap-2 text-[10px] text-slate-400">
            {Object.entries(c.rules).map(([k, n]) => <span key={k} className={CLS[k]}>{data.classes[k]}: {n}</span>)}
            <span>Pending: {c.pending}</span><span data-testid={`gtfr-exec-${c.module}`}>Executable: {c.executable_rules}</span>
          </div>
        </button>
      ))}
    </div>
  );
}

export function SourcePanel({ module, detail }) {
  const [text, setText] = useState("");
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState(false);
  const refresh = useRefresh();
  const locked = ["APPROVED", "FROZEN"].includes(detail.chapter.state);
  const onFile = async (e) => { const f = e.target.files?.[0]; if (f) setText(await f.text()); };
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/gtf-registry/chapters/${module}/sources`, { text });
      toast.success(data.duplicate ? `Identical to version ${data.version}; nothing new stored` : `Stored as version ${data.version}`);
      setText(""); refresh();
    } catch (e) { fail(e); } finally { setBusy(false); }
  };
  const open = async (v) => { try { setView((await api.get(`/gtf-registry/chapters/${module}/sources/${v}`)).data); } catch (e) { fail(e); } };
  return (
    <div className="space-y-3">
      {!locked && (
        <div className="space-y-2">
          <Textarea data-testid="gtfr-source-text" value={text} onChange={(e) => setText(e.target.value)} rows={6} placeholder="Paste the original transcription exactly as received…" className="text-xs font-mono" />
          <div className="flex flex-wrap items-center gap-2">
            <input data-testid="gtfr-source-file" type="file" accept=".txt,.md,text/plain" onChange={onFile} className="text-[11px] text-slate-400" />
            <Button data-testid="gtfr-source-submit" size="sm" disabled={busy || !text.trim()} onClick={submit}>{busy ? "Storing…" : "Store transcription"}</Button>
            <span className="text-[11px] text-slate-500">{text.length.toLocaleString()} characters</span>
          </div>
        </div>
      )}
      {locked && <div data-testid="gtfr-source-locked" className="text-[11px] text-amber-200">Chapter is {detail.chapter.state}. Reopen it to add a new transcription version.</div>}
      <table data-testid="gtfr-sources" className="w-full text-[11px] text-slate-300">
        <thead className="text-slate-500"><tr><th className="text-left px-2">Ver</th><th className="text-left px-2">SHA-256</th><th className="text-left px-2">Chars</th><th className="text-left px-2">Received</th><th /></tr></thead>
        <tbody>{detail.sources.map((s) => (
          <tr key={s.version} data-testid={`gtfr-source-${s.version}`}><td className={td}>v{s.version}</td><td className={`${td} font-mono break-all`}>{s.sha256}</td><td className={td}>{s.chars}</td><td className={td}>{utc(s.received_at)}</td>
            <td className={td}><button data-testid={`gtfr-source-view-${s.version}`} className="text-sky-300 hover:underline" onClick={() => open(s.version)}>View</button></td></tr>))}
        </tbody>
      </table>
      {detail.sources.length === 0 && <div className="text-[11px] text-slate-500">No transcription stored yet.</div>}
      {view && (
        <div data-testid="gtfr-source-viewer" className="rounded border border-[var(--border-subtle)] p-2">
          <div className="text-[10px] text-slate-500 mb-1">v{view.version} · fingerprint {view.verified ? "verified" : "MISMATCH"} <button className="ml-2 text-sky-300" onClick={() => setView(null)}>Close</button></div>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap text-[11px] text-slate-300">{view.text}</pre>
        </div>
      )}
    </div>
  );
}

export function AddRuleForm({ module, detail, classes }) {
  const latestSrc = detail.sources[0]?.version;
  const blank = { rule_key: "", classification: module === 0 ? "educational" : "unresolved", statement: "", timestamp: "", quote: "", note: "" };
  const [f, setF] = useState(blank);
  const [code, setCode] = useState("");
  const refresh = useRefresh();
  const allowed = Object.keys(classes).filter((k) => detail.chapter.signals_allowed || !["mandatory", "optional_confirmation"].includes(k));
  const prev = detail.rules.find((r) => r.rule_key === f.rule_key && r.is_latest);
  const needsCode = prev?.approval_status === "approved";
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    try {
      const body = { ...f, source_version: latestSrc, note: f.note || null };
      if (needsCode) body.step_up_token = await stepUp(code);
      const { data } = await api.post(`/gtf-registry/chapters/${module}/rules`, body);
      toast.success(`Recorded ${data.rid}`); setF(blank); setCode(""); refresh();
    } catch (e) { fail(e); }
  };
  if (!latestSrc || ["APPROVED", "FROZEN"].includes(detail.chapter.state)) return null;
  return (
    <div data-testid="gtfr-rule-form" className="grid gap-2 md:grid-cols-2">
      <Input data-testid="gtfr-rule-key" value={f.rule_key} onChange={set("rule_key")} placeholder="rule key, e.g. intro-purpose" className="h-8 text-xs font-mono" />
      <select data-testid="gtfr-rule-class" value={f.classification} onChange={set("classification")} className={sel}>{allowed.map((k) => <option key={k} value={k}>{classes[k]}</option>)}</select>
      <Textarea data-testid="gtfr-rule-statement" value={f.statement} onChange={set("statement")} rows={2} placeholder="What the passage says (no added interpretation)" className="text-xs md:col-span-2" />
      <Textarea data-testid="gtfr-rule-quote" value={f.quote} onChange={set("quote")} rows={2} placeholder={`Exact quote from transcription v${latestSrc}`} className="text-xs md:col-span-2" />
      <Input data-testid="gtfr-rule-ts" value={f.timestamp} onChange={set("timestamp")} placeholder={`timestamp within ${detail.chapter.time_range}`} className="h-8 text-xs font-mono" />
      <Input data-testid="gtfr-rule-note" value={f.note} onChange={set("note")} placeholder="Review note (optional, e.g. why unclear)" className="h-8 text-xs" />
      <div className="flex items-center gap-2 md:col-span-2">
        {needsCode && <><span className="text-[11px] text-amber-200">Changing an approved rule needs a fresh code:</span><CodeInput value={code} onChange={setCode} testId="gtfr-rule-code" /></>}
        <Button data-testid="gtfr-rule-submit" size="sm" disabled={!f.rule_key || !f.statement || !f.quote || !f.timestamp || (needsCode && code.length !== 6)} onClick={submit}>Record rule version</Button>
      </div>
    </div>
  );
}

function RuleActions({ r, locked }) {
  const [code, setCode] = useState("");
  const [reason, setReason] = useState("");
  const refresh = useRefresh();
  const act = async (fn) => { try { await fn(); refresh(); } catch (e) { fail(e); } };
  if (locked) return null;
  if (r.approval_status === "pending") return (
    <div className="flex flex-wrap gap-1">
      {r.classification !== "unresolved" && <><CodeInput value={code} onChange={setCode} testId={`gtfr-approve-code-${r.rid}`} />
        <Button data-testid={`gtfr-approve-${r.rid}`} size="sm" className="h-8" disabled={code.length !== 6} onClick={() => act(async () => api.post(`/gtf-registry/rules/${r.rid}/approve`, { step_up_token: await stepUp(code) }))}>Approve</Button></>}
      <Input data-testid={`gtfr-reject-reason-${r.rid}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="reject reason" className="h-8 w-32 text-xs" />
      <Button data-testid={`gtfr-reject-${r.rid}`} size="sm" variant="outline" className="h-8" disabled={reason.length < 3} onClick={() => act(() => api.post(`/gtf-registry/rules/${r.rid}/reject`, { reason }))}>Reject</Button>
    </div>);
  const next = { not_implemented: "implemented", implemented: "tested" }[r.implementation_status];
  if (r.approval_status === "approved" && next) return (
    <Button data-testid={`gtfr-impl-${r.rid}`} size="sm" variant="outline" className="h-8" onClick={() => act(() => api.post(`/gtf-registry/rules/${r.rid}/implementation`, { status: next }))}>Mark {next}</Button>);
  return null;
}

export function RulesTable({ detail, classes }) {
  const [cls, setCls] = useState("all");
  const locked = ["APPROVED", "FROZEN"].includes(detail.chapter.state);
  const rows = detail.rules.filter((r) => cls === "all" || r.classification === cls);
  return (
    <div className="space-y-2">
      <select data-testid="gtfr-rule-filter" value={cls} onChange={(e) => setCls(e.target.value)} className={sel}><option value="all">All classifications</option>{Object.entries(classes).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
      <div className="overflow-x-auto">
        <table data-testid="gtfr-rules" className="w-full min-w-[760px] text-[11px] text-slate-300">
          <thead className="text-slate-500"><tr>{["Rule version", "Classification", "Statement & source", "Approval", "Implementation", "Executable", ""].map((h) => <th key={h} className="text-left px-2">{h}</th>)}</tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.rid} data-testid={`gtfr-rule-${r.rid}`} className={r.is_latest ? "" : "opacity-50"}>
              <td className={`${td} font-mono`}>{r.rid}{!r.is_latest && <div className="text-slate-500">superseded</div>}{!r.integrity_ok && <div className="text-rose-300">INTEGRITY FAIL</div>}</td>
              <td className={`${td} ${CLS[r.classification]}`}>{classes[r.classification]}</td>
              <td className={td}><div className="text-slate-200">{r.statement}</div><div className="text-slate-500">v{r.source_ref.source_version} @ {r.source_ref.timestamp} — “{r.source_ref.quote}”</div>{r.note && <div className="text-fuchsia-300">Note: {r.note}</div>}</td>
              <td className={`${td} ${APPR[r.approval_status]}`} data-testid={`gtfr-rule-approval-${r.rid}`}>{r.approval_status}</td>
              <td className={td}>{r.implementation_status}</td>
              <td className={td} data-testid={`gtfr-rule-exec-${r.rid}`}>{r.executable ? "YES" : "no"}</td>
              <td className={td}>{r.is_latest && <RuleActions r={r} locked={locked} />}</td>
            </tr>))}
          </tbody>
        </table>
      </div>
      {rows.length === 0 && <div className="text-[11px] text-slate-500">No rule entries yet.</div>}
    </div>
  );
}

export function Lifecycle({ module, detail, states }) {
  const [code, setCode] = useState("");
  const [reason, setReason] = useState("");
  const refresh = useRefresh();
  const st = detail.chapter.state;
  const next = st ? states[states.indexOf(st) + 1] : null;
  const mfa = ["APPROVED", "FROZEN"].includes(next);
  const go = async () => {
    try {
      await api.post(`/gtf-registry/chapters/${module}/advance`, { to: next, ...(mfa ? { step_up_token: await stepUp(code) } : {}) });
      toast.success(`Chapter moved to ${next}`); setCode(""); refresh();
    } catch (e) { fail(e); }
  };
  const reopen = async () => {
    try { await api.post(`/gtf-registry/chapters/${module}/reopen`, { reason, step_up_token: await stepUp(code) }); toast.success("Reopened as a new chapter version"); setCode(""); setReason(""); refresh(); } catch (e) { fail(e); }
  };
  return (
    <div className="space-y-3">
      <Stepper states={states} state={st} />
      <div className="text-[11px] text-slate-500">Chapter version {detail.chapter.chapter_version}</div>
      {next && (
        <div className="flex flex-wrap items-center gap-2">
          {mfa && <CodeInput value={code} onChange={setCode} testId="gtfr-advance-code" />}
          <Button data-testid="gtfr-advance" size="sm" disabled={mfa && code.length !== 6} onClick={go}>Move to {next}</Button>
        </div>
      )}
      {["APPROVED", "FROZEN"].includes(st) && (
        <div className="flex flex-wrap items-center gap-2">
          <Input data-testid="gtfr-reopen-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for a new version" className="h-8 w-56 text-xs" />
          <CodeInput value={code} onChange={setCode} testId="gtfr-reopen-code" />
          <Button data-testid="gtfr-reopen" size="sm" variant="outline" disabled={reason.length < 3 || code.length !== 6} onClick={reopen}>Reopen (new version)</Button>
        </div>
      )}
      <ul data-testid="gtfr-events" className="space-y-0.5 text-[11px] text-slate-400">
        {detail.events.map((e) => <li key={e.seq}>#{e.seq} · {utc(e.at)} · v{e.chapter_version} → {e.state}{e.mfa ? " · code verified" : ""}{e.snapshot_hash && <span className="font-mono text-slate-500"> · snapshot {e.snapshot_hash.slice(0, 16)}…</span>}</li>)}
      </ul>
    </div>
  );
}

export function IntegrityBanner({ integrity }) {
  if (integrity.ok) return <div data-testid="gtfr-integrity-ok" className="text-[11px] text-emerald-300">Integrity verified · signing key {integrity.key.key_id}</div>;
  return (
    <div data-testid="gtfr-integrity-fail" className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-xs text-rose-200">
      Registry changes and governance actions are blocked: {integrity.key.ready ? "integrity verification failed" : "the registry signing key is not configured on this server"}.
      {integrity.problems.length > 0 && <ul className="mt-1 list-disc pl-4">{integrity.problems.map((p) => <li key={p}>{p}</li>)}</ul>}
    </div>
  );
}

export function AuditList() {
  const { data } = useQuery({ queryKey: ["gtfr-audit"], queryFn: async () => (await api.get("/gtf-registry/audit")).data });
  if (!data) return null;
  return (
    <div className="space-y-1 text-[11px] text-slate-400">
      <div data-testid="gtfr-chain" className={data.chain.ok ? "text-emerald-300" : "text-rose-300"}>Audit chain {data.chain.ok ? "verified (signed)" : !data.chain.verifiable ? "cannot be verified: signing key not configured" : `broken at #${data.chain.broken_at}`} · {data.chain.records} record(s)</div>
      {data.chain.head && <div className="font-mono text-[10px] text-slate-500 break-all">Latest seal: {data.chain.head}</div>}
      <ul data-testid="gtfr-audit" className="max-h-56 overflow-auto">{data.rows.map((r) => <li key={r.seq}>#{r.seq} · {utc(r.created_at)} · {r.action}</li>)}</ul>
    </div>
  );
}
